/**
 * <product-card-slider> — swipeable image slider for the product card.
 *
 * Splide is not requested until a card is about to enter the viewport, and the
 * library URL travels on the element as data-splide-src (Liquid owns
 * asset_url). Cards below the fold on a long collection page therefore cost
 * nothing until they are scrolled to.
 *
 * With data-hint="true" the images nudge sideways to signal that they swipe.
 * Three things trigger it, all cancelled by a real drag and skipped under a
 * reduced-motion preference:
 *   - the first slider on the page, shortly after it mounts;
 *   - a card hovered anywhere for DWELL_DELAY without being touched;
 *   - a card held under a finger for DWELL_DELAY on mobile.
 * A card that has hinted keeps its arrows on show, and a card whose slider the
 * visitor has already moved stops hinting — the point is made.
 */
if (!customElements.get('product-card-slider')) {
  const HINT_DELAY = 900;
  const DWELL_DELAY = 1500;
  const DRAG_THRESHOLD = 8;
  // Safety net for the second image, in case the first never reports back.
  const SECOND_SLIDE_TIMEOUT = 3000;

  let splideLoader = null;
  let firstHintPlayed = false;

  /**
   * Appends its own tag rather than waiting on a matching one already in the
   * document: a page can carry several sections that each queue splide.min.js,
   * and a tag that has already executed never fires load again, which would hang
   * this promise forever. A second tag for the same URL is served from cache and
   * only re-runs a UMD bundle that reassigns its own global, so the duplicate
   * costs nothing.
   */
  function loadSplide(src) {
    if (window.Splide) return Promise.resolve();
    if (splideLoader) return splideLoader;

    splideLoader = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', reject, { once: true });
      script.src = src;
      document.head.appendChild(script);
    });

    return splideLoader;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  class ProductCardSlider extends HTMLElement {
    connectedCallback() {
      if (this.dataset.sliderInit) return;
      this.dataset.sliderInit = 'true';

      this.count = parseInt(this.dataset.count, 10) || 0;
      if (this.count <= 1) return;

      this.observe();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      clearTimeout(this.hintTimer);
      clearTimeout(this.dwellTimer);
      clearTimeout(this.secondSlideTimer);
      this.splide?.destroy();
    }

    observe() {
      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.load();
        },
        { rootMargin: '300px 0px' }
      );

      this.observer.observe(this);
    }

    load() {
      loadSplide(this.dataset.splideSrc)
        .then(() => this.mount())
        // Library unavailable: the slides stay a plain horizontal scroll row.
        .catch(() => this.classList.add('product-card-slider--static'));
    }

    mount() {
      const track = this.querySelector('.product-card-slider--track');
      const list = this.querySelector('.product-card-slider--list');
      if (!track || !list) return;

      this.classList.add('splide');
      track.classList.add('splide__track');
      list.classList.add('splide__list');
      this.querySelectorAll('.product-card-slider--slide').forEach((slide) => {
        slide.classList.add('splide__slide');
      });

      this.splide = new Splide(this, {
        // 'slide' + rewind rather than 'loop': a loop clones every slide,
        // doubling the DOM of every card in a grid for no gain here.
        type: 'slide',
        rewind: true,
        perPage: 1,
        perMove: 1,
        // Both off: the markup carries its own arrows so they can be styled
        // and positioned with the rest of the card.
        arrows: false,
        pagination: false,
        drag: this.dataset.drag !== 'false',
        speed: 400,
        // Slides from the third on carry data-splide-lazy; 'nearby' with
        // perPage 1 and preloadPages 1 gives a load range of one slide either
        // side of the active one, so the third image arrives as the visitor
        // reaches the second.
        lazyLoad: 'nearby',
        preloadPages: 1,
      });

      // Slides with data-src are lazysizes' job, but it will not unveil them
      // on its own — they sit outside the clipped track and Splide moves them
      // by transform, which fires no scroll event — so pull one in whenever it
      // comes into play. Slides from the third on are Splide's to load.
      this.splide.on('active visible', (slide) => this.unveil(slide.slide));
      this.splide.mount();

      this.queueSecondSlide();

      this.bindArrows();
      this.trackPointer();
      this.setupHints();
    }

    bindArrows() {
      const prev = this.querySelector('.product-card-slider--prev');
      const next = this.querySelector('.product-card-slider--next');
      if (!prev || !next) return;

      // The arrows overlay the slide links, so swallow the event rather than
      // letting it bubble on to a card-level handler. Their hit slop reaches
      // well past the icon, so a swipe that happens to start on one must drag
      // the slider without also paging it.
      const paginate = (direction) => (event) => {
        event.preventDefault();
        if (this.dragged) return;
        this.splide.go(direction);
      };

      prev.addEventListener('click', paginate('<'));
      next.addEventListener('click', paginate('>'));

      this.classList.add('product-card-slider--has-arrows');
    }

    /**
     * One image at a time, in order: the second slide is only fetched once the
     * first has finished, so the two never compete for bandwidth while the card
     * is painting. Both are lazysizes-managed, so the signal is its lazyloaded
     * event on the primary image; the class covers an image that finished
     * before this ran, and the timeout covers lazysizes never reporting.
     */
    queueSecondSlide() {
      const second = this.querySelectorAll('.product-card-slider--slide')[1];
      if (!second) return;

      const primary = this.querySelector('.product-primary-image');
      if (!primary || primary.classList.contains('lazyloaded')) {
        this.unveil(second);
        return;
      }

      let promoted = false;
      const promote = () => {
        if (promoted) return;
        promoted = true;
        clearTimeout(this.secondSlideTimer);
        this.unveil(second);
      };

      primary.addEventListener('lazyloaded', promote, { once: true });
      primary.addEventListener('load', () => {
        // The placeholder fires load too; only the real source counts.
        if (primary.classList.contains('lazyloaded')) promote();
      });

      this.secondSlideTimer = setTimeout(promote, SECOND_SLIDE_TIMEOUT);
    }

    unveil(slide) {
      slide.querySelectorAll('img[data-src]').forEach((img) => {
        if (window.lazySizes) {
          window.lazySizes.loader.unveil(img);
        } else {
          img.src = img.dataset.src;
        }
      });
    }

    /**
     * One set of pointer listeners feeds both the click guard and the dwell
     * hint: a press that travels is a drag, a press that stays put is a visitor
     * dwelling on the card.
     *
     * They go on the whole card, not just the images. Someone reading the title
     * or the price is considering that product, which is exactly when the hint
     * is worth showing — and it matches the arrows, which the CSS already
     * reveals on card hover. Pointer events from the images bubble up here, so
     * drag detection is unaffected.
     */
    trackPointer() {
      const card = this.closest('.product-card') || this;
      let startX = 0;

      card.addEventListener('pointerenter', () => this.scheduleDwellHint());

      card.addEventListener('pointerleave', () => {
        clearTimeout(this.dwellTimer);
      });

      card.addEventListener('pointerdown', (event) => {
        startX = event.clientX;
        this.pointerDown = true;
        this.dragged = false;
        // Touch fires enter and down together; a held finger restarts the
        // clock so the dwell is measured from the press, not the hover.
        this.scheduleDwellHint();
      });

      card.addEventListener('pointermove', (event) => {
        if (!this.pointerDown) return;
        if (Math.abs(event.clientX - startX) <= DRAG_THRESHOLD) return;
        this.dragged = true;
        this.stopHinting(true);
      });

      const release = () => {
        this.pointerDown = false;
        clearTimeout(this.dwellTimer);
      };

      card.addEventListener('pointerup', release);
      card.addEventListener('pointercancel', release);

      // A drag that ends on a slide must not navigate to the product.
      this.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', (event) => {
          if (this.dragged) event.preventDefault();
        });
      });
    }

    setupHints() {
      this.hintEnabled = this.dataset.hint === 'true' && !prefersReducedMotion();
      if (!this.hintEnabled) return;

      // Once the visitor has moved the slider themselves — swipe or arrow —
      // there is nothing left to hint at.
      this.splide.on('drag move', () => this.stopHinting(true));

      if (firstHintPlayed) return;
      firstHintPlayed = true;
      this.scheduleHint(HINT_DELAY);
    }

    scheduleDwellHint() {
      if (!this.hintEnabled) return;
      clearTimeout(this.dwellTimer);
      this.dwellTimer = setTimeout(() => this.runHint(), DWELL_DELAY);
    }

    scheduleHint(delay) {
      clearTimeout(this.hintTimer);
      this.hintTimer = setTimeout(() => this.runHint(), delay);
    }

    /**
     * The nudge itself. It animates the same transform Splide keeps inline on
     * the list, so it may only run while the slider is at rest.
     */
    runHint() {
      const list = this.querySelector('.splide__list');
      if (!list || !this.hintEnabled || this.hinting || this.interacted) return;

      this.hinting = true;
      // The hinted card also keeps its arrows on show, hover or not: it is the
      // card being pointed at, so the affordance belongs with it.
      this.classList.add('product-card-slider--hinted');

      list.addEventListener(
        'animationend',
        () => {
          list.classList.remove('is-hinting');
          this.hinting = false;
        },
        { once: true }
      );

      list.classList.add('is-hinting');
    }

    stopHinting(interacted) {
      clearTimeout(this.hintTimer);
      clearTimeout(this.dwellTimer);
      this.querySelector('.splide__list')?.classList.remove('is-hinting');
      this.hinting = false;
      if (interacted) this.interacted = true;
    }
  }

  customElements.define('product-card-slider', ProductCardSlider);
}
